import { useState, useMemo, useRef, useEffect } from "react";
import { computeSheet, colName, emptyCells } from "@/lib/sheetEngine";
import { Table2, Plus, Minus, Rows3, Columns3, Trash2, Link2, Bookmark } from "lucide-react";

const MAX_ROWS = 100;
const MAX_COLS = 26;

// Hoja de cálculo embebida dentro de una nota.
// raw: string JSON con { id, cells }. editable: permite editar y persistir via onChange(id, cells).
export function EmbeddedSheet({ raw, editable = false, onChange, onRemove, onSaveTemplate, badge }) {
  const parsed = useMemo(() => {
    try {
      const o = JSON.parse(String(raw).trim());
      const cells = Array.isArray(o.cells) && o.cells.length ? o.cells : emptyCells(4, 3);
      return { id: o.id || "s_unknown", cells };
    } catch {
      return { id: "s_unknown", cells: emptyCells(4, 3) };
    }
  }, [raw]);

  const id = parsed.id;
  // Estado local para edición fluida (no se re-sincroniza con raw tras montar).
  const [cells, setCells] = useState(parsed.cells);
  const [focus, setFocus] = useState(null); // "r:c"
  const inputsRef = useRef({});
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const firstPush = useRef(true);

  useEffect(() => {
    if (firstPush.current) { firstPush.current = false; return; }
    onChangeRef.current?.(id, cells);
  }, [cells, id]);

  const { display } = useMemo(() => computeSheet(cells), [cells]);
  const nrows = cells.length;
  const ncols = cells[0]?.length || 0;

  const setCell = (r, c, v) => {
    setCells((prev) => {
      const next = prev.map((row) => row.slice());
      next[r][c] = v;
      return next;
    });
  };

  const addRow = () => { if (nrows >= MAX_ROWS) return; setCells((p) => [...p, Array.from({ length: ncols }, () => "")]); };
  const addCol = () => { if (ncols >= MAX_COLS) return; setCells((p) => p.map((row) => [...row, ""])); };
  const delRow = () => { if (nrows <= 1) return; setCells((p) => p.slice(0, -1)); };
  const delCol = () => { if (ncols <= 1) return; setCells((p) => p.map((row) => row.slice(0, -1))); };

  const focusCell = (r, c) => { inputsRef.current[`${r}:${c}`]?.focus(); };
  const onKeyDown = (e, r, c) => {
    if (e.key === "Enter" || e.key === "ArrowDown") { e.preventDefault(); focusCell(Math.min(r + 1, nrows - 1), c); }
    else if (e.key === "ArrowUp") { e.preventDefault(); focusCell(Math.max(r - 1, 0), c); }
    else if (e.key === "Tab") { e.preventDefault(); focusCell(r, c + 1 < ncols ? c + 1 : c); }
  };

  return (
    <div className="not-prose my-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 overflow-hidden" data-testid={`embedded-sheet-${id}`}>
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60">
        <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400"><Table2 size={15} /> Hoja de cálculo</span>
        {badge && (
          <a href={badge.href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-500 hover:bg-indigo-500/20 transition-colors" title="Vinculada — se sincroniza con la hoja original" data-testid="sheet-linked-badge">
            <Link2 size={11} /> {badge.label}
          </a>
        )}
        {editable && (
          <div className="flex flex-wrap items-center gap-1 ml-auto">
            <IconMini onClick={addRow} title="Añadir fila" testid={`sheet-add-row-${id}`}><Plus size={12} /><Rows3 size={12} /></IconMini>
            <IconMini onClick={delRow} title="Quitar fila" testid={`sheet-del-row-${id}`}><Minus size={12} /><Rows3 size={12} /></IconMini>
            <IconMini onClick={addCol} title="Añadir columna" testid={`sheet-add-col-${id}`}><Plus size={12} /><Columns3 size={12} /></IconMini>
            <IconMini onClick={delCol} title="Quitar columna" testid={`sheet-del-col-${id}`}><Minus size={12} /><Columns3 size={12} /></IconMini>
            {onSaveTemplate && (
              <button onClick={() => onSaveTemplate(cells)} title="Guardar como plantilla" className="ml-1 inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-indigo-500 hover:bg-indigo-500/10 transition-colors" data-testid={`sheet-save-template-${id}`}><Bookmark size={13} /></button>
            )}
            {onRemove && (
              <button onClick={() => onRemove(id)} title="Eliminar hoja" className="ml-0.5 inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors" data-testid={`sheet-remove-${id}`}><Trash2 size={13} /></button>
            )}
          </div>
        )}
      </div>

      <div className="overflow-auto">
        <table className="border-collapse text-sm w-full">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 w-9 min-w-[36px]"></th>
              {Array.from({ length: ncols }, (_, c) => (
                <th key={c} className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono2 text-[11px] text-slate-500 min-w-[88px] px-2 py-1.5">{colName(c)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cells.map((row, r) => (
              <tr key={r}>
                <td className="sticky left-0 z-10 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono2 text-[11px] text-slate-500 text-center w-9 min-w-[36px]">{r + 1}</td>
                {row.map((_, c) => {
                  const key = `${r}:${c}`;
                  const editing = editable && focus === key;
                  const shown = editing ? cells[r][c] : (display[r]?.[c] ?? "");
                  const isErr = typeof shown === "string" && (shown === "#ERROR" || shown === "#CICLO");
                  return (
                    <td key={c} className="border border-slate-200 dark:border-slate-800 p-0">
                      {editable ? (
                        <input
                          ref={(el) => { inputsRef.current[key] = el; }}
                          value={shown}
                          onFocus={() => setFocus(key)}
                          onBlur={() => setFocus(null)}
                          onChange={(e) => setCell(r, c, e.target.value)}
                          onKeyDown={(e) => onKeyDown(e, r, c)}
                          className={`w-full min-w-[88px] px-2 py-1.5 bg-transparent focus:bg-indigo-50 dark:focus:bg-indigo-500/10 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-indigo-500 font-mono2 text-[13px] ${isErr ? "text-rose-500" : "text-slate-800 dark:text-slate-200"}`}
                          data-testid={`sheet-cell-${colName(c)}${r + 1}`}
                        />
                      ) : (
                        <div className={`min-w-[88px] px-2 py-1.5 font-mono2 text-[13px] ${isErr ? "text-rose-500" : "text-slate-800 dark:text-slate-200"}`}>{shown}</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editable && (
        <div className="px-3 py-1.5 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60">
          <span className="font-mono2 text-[10px] text-slate-400">Fórmulas: <code className="text-emerald-500">=A1+B2</code>, <code className="text-emerald-500">=SUMA(A1:A5)</code>, <code className="text-emerald-500">=PROMEDIO(B1:B3)</code></span>
        </div>
      )}
    </div>
  );
}

function IconMini({ onClick, title, testid, children }) {
  return (
    <button type="button" onClick={onClick} title={title} data-testid={testid} className="inline-flex items-center justify-center gap-0.5 h-7 px-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
      {children}
    </button>
  );
}
